/**
 * SOURCE DE VÉRITÉ — ports de développement local ITaaS Suite.
 *
 * Règles :
 * - Ne jamais assigner un port déjà `active` ou `reserved` à un autre produit.
 * - APIs produit : bloc 3010–3099 (pair API/web quand possible : 3020/3021).
 * - Plateforme : 3000 (Suite), 3030 (IAM).
 * - Fronts Vite legacy : 5173+ (à migrer vers 30xx quand possible).
 * - Infra Docker dédiée : 543x (Postgres), 638x (Redis).
 *
 * Avant d'ajouter un port : `pnpm check` dans products/itaas-ports.
 */
export const ITAAS_PORT_REGISTRY = {
    $schema: "./registry.schema.json",
    version: "1.0.0",
    updatedAt: "2026-07-05",
    host: "localhost",
    conventions: [
        "Une seule source : @itaas/ports (products/itaas-ports).",
        "APIs NestJS : variable API_PORT + défaut dans main.ts depuis getPort().",
        "Fronts Vite : port dans vite.config.ts depuis getPort() ou import statique documenté.",
        "Proxies Vite : cibler l'URL API via localServiceUrl() — ne pas hardcoder un port divergent.",
        "Nouveau produit : réserver un bloc dans ce fichier AVANT le premier commit.",
        "En cas de port occupé sur la machine : changer UNIQUEMENT via ce registre, pas en local silencieux.",
    ],
    products: {
        suite: {
            web: {
                port: 3000,
                envVars: ["PORT", "VITE_PORT"],
                urlEnvVar: "VITE_SUITE_URL",
                status: "active",
                description: "Portail Suite — login et choix de module",
                productPath: "products/itaas-suite/apps/web",
                notes: "Point d'entrée utilisateur. Si 3000 pris, Vite incrémente — préférer libérer 3000.",
            },
        },
        erp: {
            api: {
                port: 3010,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API ERP (produit autonome)",
                productPath: "products/itaas-erp/apps/api",
            },
            web: {
                port: 5173,
                envVars: ["WEB_PORT", "VITE_PORT"],
                urlEnvVar: "VITE_ERP_URL",
                status: "active",
                description: "UI ERP (Vite)",
                productPath: "products/itaas-erp/apps/web",
                notes: "Bande 517x legacy — migration future vers 3011 possible.",
            },
        },
        inventory: {
            api: {
                port: 3020,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "ISC API — moteur stock (métier)",
                productPath: "products/itaas-inventory/apps/api",
            },
            web: {
                port: 3021,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_INVENTORY_URL",
                status: "active",
                description: "UI Inventory — handoff depuis Suite",
                productPath: "products/itaas-inventory/apps/web",
            },
        },
        product: {
            api: {
                port: 3022,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Product (catalogue média / PIM)",
                productPath: "products/itaas-product/apps/api",
            },
            web: {
                port: 3023,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_PRODUCT_URL",
                status: "active",
                description: "UI Product",
                productPath: "products/itaas-product/apps/web",
            },
        },
        iam: {
            api: {
                port: 3030,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "IAM — identity, JWT, permissions, modules tenant",
                productPath: "products/itaas-iam/apps/api",
                notes: "Seule source users/JWT pour la Suite.",
            },
        },
        notifications: {
            api: {
                port: 3040,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Notifications multi-canal",
                productPath: "products/itaas-notifications/apps/api",
            },
        },
        security: {
            web: {
                port: 3050,
                envVars: ["VITE_PORT", "PORT"],
                urlEnvVar: "VITE_SECURITY_URL",
                status: "active",
                description: "UI Security (handoff Suite)",
                productPath: "products/itaas-security",
                notes: "Port 3050 — ne plus utiliser 3010 (conflit ERP API).",
            },
        },
        rh: {
            api: {
                port: 3060,
                envVars: ["API_PORT", "PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API RH (NestJS — careers, notifications)",
                productPath: "products/itaas-rh/apps/api",
            },
            web: {
                port: 5174,
                envVars: ["VITE_PORT", "PORT"],
                urlEnvVar: "VITE_RH_URL",
                status: "active",
                description: "UI RH (Vite)",
                productPath: "products/itaas-rh",
                notes: "Bande 517x legacy.",
            },
        },
        biometricGateway: {
            api: {
                port: 3081,
                envVars: ["API_PORT", "PORT"],
                urlEnvVar: "VITE_BIOMETRIC_GATEWAY_URL",
                basePath: "/",
                status: "active",
                description: "Passerelle terminaux biométriques (ZKTeco)",
                productPath: "products/itaas-biometric-gateway/apps/api",
            },
        },
        billing: {
            api: {
                port: 3090,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Billing — moteur financier transverse",
                productPath: "products/itaas-billing/apps/api",
            },
            web: {
                port: 3091,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_BILLING_URL",
                status: "active",
                description: "UI Billing (Kanakku)",
                productPath: "products/itaas-billing/apps/web",
            },
        },
        hospitol: {
            api: {
                port: 3092,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Hospitol — orchestration hospitalière",
                productPath: "products/itaas-pharmacy/apps/api",
            },
            web: {
                port: 3093,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_HOSPITOL_URL",
                status: "active",
                description: "UI Hospitol — plateforme hospitalière modulaire",
                productPath: "products/itaas-pharmacy/apps/web",
            },
            printBridge: {
                port: 9310,
                envVars: ["PRINT_BRIDGE_PORT"],
                urlEnvVar: "VITE_PRINT_BRIDGE_URL",
                status: "active",
                description: "ITaaS Print Bridge — impression locale TSPL/RAW (Windows)",
                productPath: "products/itaas-pharmacy/apps/print-bridge",
                notes: "Service localhost uniquement — ne pas exposer sur le réseau.",
            },
        },
        legal: {
            api: {
                port: 3094,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "reserved",
                description: "API Legal — gestion cabinet d'avocats (LPMS)",
                productPath: "products/itaas-legal/apps/api",
                notes: "Scaffold Sprint L0 — passer en active après premier commit apps/api.",
            },
            web: {
                port: 3095,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_LEGAL_URL",
                status: "reserved",
                description: "UI Legal — cabinet avocat, dossiers et honoraires",
                productPath: "products/itaas-legal/apps/web",
                notes: "Scaffold Sprint L0 — passer en active après premier commit apps/web.",
            },
        },
        edu: {
            api: {
                port: 3096,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Edu — gestion scolaire (EMS)",
                productPath: "products/itaas-edu/apps/api",
            },
            web: {
                port: 3097,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_EDU_URL",
                status: "active",
                description: "UI Edu — établissement scolaire modulaire",
                productPath: "products/itaas-edu/apps/web",
            },
        },
        mineportal: {
            api: {
                port: 3098,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "reserved",
                description: "API Mining Company Portal — orchestration minière RDC",
                productPath: "products/itaas-mineportal/apps/api",
                notes: "Scaffold Sprint M0 — passer en active après premier commit apps/api.",
            },
            web: {
                port: 3099,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_MINEPORTAL_URL",
                status: "reserved",
                description: "UI Mining Company Portal — RH, magasin, validations, sous-traitance",
                productPath: "products/itaas-mineportal/apps/web",
                notes: "Scaffold Sprint M0 — passer en active après premier commit apps/web.",
            },
        },
        autoid: {
            api: {
                port: 3070,
                envVars: ["API_PORT", "PORT"],
                basePath: "/api",
                status: "active",
                description: "API AutoID — scan, codes-barres, QR",
                productPath: "products/itaas-autoid/apps/api",
            },
        },
        pep: {
            api: {
                port: 3100,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API PEP — catalogue produits, pricing, docs",
                productPath: "products/itaas-product-experience-platform/apps/api",
            },
            web: {
                port: 3101,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_PEP_URL",
                status: "active",
                description: "Web PEP — Product Experience Platform publique",
                productPath: "products/itaas-product-experience-platform/apps/web",
            },
        },
        subportal: {
            api: {
                port: 3102,
                envVars: ["API_PORT", "PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API SubPortal — portail sous-traitant ARSP",
                productPath: "products/itaas-subportal/apps/api",
            },
            web: {
                port: 3103,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_SUBPORTAL_URL",
                status: "active",
                description: "Web SubPortal — handoff depuis Suite",
                productPath: "products/itaas-subportal/apps/web",
            },
        },
        comex: {
            api: {
                port: 3104,
                envVars: ["API_PORT", "PORT"],
                basePath: "/api/v1",
                status: "reserved",
                description: "API Comex — transit, fret & douane RDC",
                productPath: "products/itaas-comex/apps/api",
            },
            web: {
                port: 3105,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_COMEX_URL",
                status: "reserved",
                description: "Web Comex — transitaires & déclarants",
                productPath: "products/itaas-comex/apps/web",
            },
        },
        crm: {
            api: {
                port: 3106,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API CRM — contacts, pipeline, devis commerciaux",
                productPath: "products/itaas-crm/apps/api",
            },
            web: {
                port: 3107,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_CRM_URL",
                status: "active",
                description: "UI CRM Kanakku — contacts, pipeline, devis, livraisons",
                productPath: "products/itaas-crm/apps/web",
                notes: "Shell Kanakku split complet. Devis proxy Billing ; contacts/pipeline @itaas/crm-api.",
            },
        },
        finance: {
            api: {
                port: 3108,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "active",
                description: "API Finance — trésorerie et rapports",
                productPath: "products/itaas-finance/apps/api",
            },
            web: {
                port: 3109,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_FINANCE_URL",
                status: "active",
                description: "UI Finance Kanakku — trésorerie et rapports comptables",
                productPath: "products/itaas-finance/apps/web",
            },
        },
        payments: {
            api: {
                port: 3110,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "reserved",
                description: "API Payments — encaissement mobile money vers comptes marchands tenant",
                productPath: "products/itaas-payments/apps/api",
            },
            web: {
                port: 3111,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_PAYMENTS_URL",
                status: "reserved",
                description: "UI Payments — configuration comptes marchands par établissement",
                productPath: "products/itaas-payments/apps/web",
            },
        },
        procurement: {
            web: {
                port: 3112,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_PROCUREMENT_URL",
                status: "active",
                description: "UI ERP Procurement (Kanakku) — achats fournisseurs",
                productPath: "products/itaas-erp/apps/procurement-web",
                notes: "Proxy Billing API jusqu'à extraction Nest procurement.",
            },
        },
        inventoryKanakku: {
            web: {
                port: 3113,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_INVENTORY_KANAKKU_URL",
                status: "active",
                description: "UI Inventory Kanakku — stock, branches, réquisitions",
                productPath: "products/itaas-inventory/apps/inventory-kanakku-web",
                notes: "UI ISC native reste sur inventory.web :3021. Proxy Billing API.",
            },
        },
        productKanakku: {
            web: {
                port: 3114,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_PRODUCT_KANAKKU_URL",
                status: "active",
                description: "UI Product Kanakku — catalogue articles (PIM)",
                productPath: "products/itaas-product/apps/product-kanakku-web",
                notes: "UI MPM native reste sur product.web :3023. Proxy Billing API.",
            },
        },
        workforce: {
            web: {
                port: 3116,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_WORKFORCE_URL",
                status: "active",
                description: "UI ITaaS Workforce — employés & organisation (module RH dupliqué)",
                productPath: "products/itaas-rh/apps/workforce-web",
                notes: "Réutilise src/ RH + rh-api :3060. Hub complet reste sur rh.web :5174.",
            },
        },
        recruitment: {
            web: {
                port: 3117,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_RECRUITMENT_URL",
                status: "active",
                description: "UI ITaaS Recruitment — pipeline recruteur + portail /careers (module RH dupliqué)",
                productPath: "products/itaas-rh/apps/recruitment-web",
                notes: "Réutilise src/ RH + rh-api :3060. Hub complet reste sur rh.web :5174.",
            },
        },
        attendance: {
            web: {
                port: 3118,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_ATTENDANCE_URL",
                status: "active",
                description: "UI ITaaS Attendance — présence & pointage (module RH dupliqué)",
                productPath: "products/itaas-rh/apps/attendance-web",
                notes: "Réutilise src/ RH + rh-api :3060 + biometric-gateway. Hub complet reste sur rh.web :5174.",
            },
        },
        insurance: {
            web: {
                port: 3119,
                envVars: ["VITE_PORT", "WEB_PORT"],
                urlEnvVar: "VITE_INSURANCE_HUB_URL",
                status: "active",
                description: "UI Insurance Hub — portail central assureurs multi-hôpitaux",
                productPath: "products/itaas-insurance/apps/web",
            },
            api: {
                port: 3120,
                envVars: ["API_PORT"],
                basePath: "/api/v1",
                status: "reserved",
                description: "API Insurance Hub — partenariats et validations cross-tenant",
                productPath: "products/itaas-insurance/apps/api",
                notes: "Scaffold — démo locale via jetons ITINS et localStorage hub.",
            },
        },
    },
    infrastructure: {
        postgresDefault: {
            port: 5432,
            envVars: ["DATABASE_URL"],
            status: "active",
            description: "PostgreSQL local / partagé (schémas par produit)",
            productPath: "—",
        },
        postgresInventoryDocker: {
            port: 5433,
            envVars: ["DATABASE_URL"],
            status: "active",
            description: "PostgreSQL Docker itaas-inventory",
            productPath: "products/itaas-inventory/docker-compose.yml",
        },
        redisDefault: {
            port: 6379,
            envVars: ["REDIS_PORT", "REDIS_URL"],
            status: "active",
            description: "Redis local par défaut",
            productPath: "—",
        },
        redisInventoryDocker: {
            port: 6380,
            envVars: ["REDIS_PORT"],
            status: "active",
            description: "Redis Docker itaas-inventory",
            productPath: "products/itaas-inventory/docker-compose.yml",
        },
        redisProduct: {
            port: 6381,
            envVars: ["REDIS_PORT"],
            status: "active",
            description: "Redis dédié itaas-product",
            productPath: "products/itaas-product",
        },
        smtpDev: {
            port: 1025,
            envVars: ["SMTP_PORT"],
            status: "reserved",
            description: "Mailhog / Mailpit (dev)",
            productPath: "—",
        },
    },
};
